import { computed, shallowRef, watch, type Ref } from 'vue';
import type { ProductTourStep } from '@/constants/productTour';

interface TourRect { left: number; top: number; width: number; height: number }

// eslint-disable-next-line import/prefer-default-export
export function useTourPosition(step: Ref<ProductTourStep | null>, card: Ref<HTMLElement | null>) {
    const spotlight = shallowRef<TourRect | null>(null);
    const cardPosition = shallowRef<{ left: string; top: string } | null>(null);
    const spotlightStyle = computed(() => {
        const rect = spotlight.value;
        return rect ? {
            left: `${rect.left}px`,
            top: `${rect.top}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
        } : undefined;
    });

    watch([step, card], ([currentStep, panel], _previous, onCleanup) => {
        spotlight.value = null;
        cardPosition.value = null;
        if (!currentStep || !panel) return;

        const target = document.querySelector<HTMLElement>(`[data-product-tour="${currentStep.target}"]`);
        if (!target) return; // Keep an unanchored, dismissible card if the target disappears.

        const bounds = target.getBoundingClientRect();
        if (bounds.top < 12 || bounds.bottom > window.innerHeight - 12
            || bounds.left < 12 || bounds.right > document.documentElement.clientWidth - 12) {
            target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        }

        function update() {
            if (!target?.isConnected || !panel) {
                spotlight.value = null;
                cardPosition.value = null;
                return;
            }
            const rect = target.getBoundingClientRect();
            if (!rect.width || !rect.height) {
                spotlight.value = null;
                cardPosition.value = null;
                return;
            }
            // A stable scrollbar gutter can make the dialog narrower than 100vw.
            const viewportWidth = Math.min(
                document.documentElement.clientWidth,
                panel.closest('dialog')?.clientWidth ?? window.innerWidth,
            );
            const viewportHeight = window.innerHeight;
            const left = Math.max(6, rect.left - 6);
            const top = Math.max(6, rect.top - 6);
            const right = Math.min(viewportWidth - 6, rect.right + 6);
            const bottom = Math.min(viewportHeight - 6, rect.bottom + 6);
            spotlight.value = { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };

            const panelRect = panel.getBoundingClientRect();
            const desiredLeft = currentStep?.align === 'end' ? right - panelRect.width : left;
            const below = bottom + 12;
            const desiredTop = below + panelRect.height <= viewportHeight - 12
                ? below : top - panelRect.height - 12;
            cardPosition.value = {
                left: `${Math.max(12, Math.min(desiredLeft, viewportWidth - panelRect.width - 12))}px`,
                top: `${Math.max(12, Math.min(desiredTop, viewportHeight - panelRect.height - 12))}px`,
            };
        }

        let frame = 0;
        function scheduleUpdate() {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        }
        const observer = new ResizeObserver(scheduleUpdate);
        observer.observe(target);
        observer.observe(panel);
        observer.observe(document.body);
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, true);
        window.visualViewport?.addEventListener('resize', scheduleUpdate);
        update();
        onCleanup(() => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate, true);
            window.visualViewport?.removeEventListener('resize', scheduleUpdate);
        });
    }, { flush: 'post' });

    return { spotlightStyle, cardPosition };
}
